import { useCallback, useMemo, useState } from "react";

import { useSimulationsForProjects } from "../../api/simulations";
import { useSnapshotsForProjects } from "../../api/snapshots";
import { useStrategiesForProjects } from "../../api/strategies";
import type { FireSimulation } from "../../types/FireSimulation";
import type { Snapshot } from "../../types/snapshot";
import type { StoryContent } from "../../types/storyMap";
import { referencedProjectIds } from "./layerEntries";
import type { StoryScopeInfo } from "./useStoryScope";

export interface StrategyOption {
  id: number;
  name: string;
}

/**
 * The per-project pickers the builder offers: strategies, successful
 * simulations and snapshots, keyed by project id. Loaded for the projects
 * the story already cites plus any the author picks in a dialog
 * (`requestProject`), never for a whole folder up front.
 */
export interface ScopeLists {
  strategiesByProject: Record<string, StrategyOption[]>;
  /** Strategy id → name across every loaded project (legend rows, chips). */
  strategyNames: Record<string, string>;
  simulationsByProject: Record<string, FireSimulation[]>;
  snapshotsByProject: Record<string, Snapshot[]>;
  /** Load the pickers for a project the author just chose. */
  requestProject: (projectId: number) => void;
  loadedProjectIds: number[];
  isPending: boolean;
}

const EMPTY_IDS: number[] = [];

export function useScopeLists(scope: StoryScopeInfo, content: StoryContent | null | undefined): ScopeLists {
  const [requested, setRequested] = useState<number[]>(EMPTY_IDS);
  const requestProject = useCallback((projectId: number) => {
    setRequested((prev) => (prev.includes(projectId) ? prev : [...prev, projectId]));
  }, []);
  const referenced = useMemo(() => referencedProjectIds(content, scope.defaultProjectId), [content, scope.defaultProjectId]);
  const ids = useMemo(() => {
    const set = new Set<number>([...referenced, ...requested]);
    // Only projects the story may actually draw from.
    return Array.from(set).filter((id) => scope.byId.has(id));
  }, [referenced, requested, scope.byId]);

  const strategies = useStrategiesForProjects(ids);
  const simulations = useSimulationsForProjects(ids);
  const snapshots = useSnapshotsForProjects(ids);

  const strategiesByProject = useMemo(() => {
    const out: Record<string, StrategyOption[]> = {};
    Object.entries(strategies.byProject).forEach(([pid, list]) => {
      out[pid] = list.map((s) => ({ id: Number(s.id), name: s.name }));
    });
    return out;
  }, [strategies.byProject]);
  const strategyNames = useMemo(() => {
    const out: Record<string, string> = {};
    Object.values(strategiesByProject).forEach((list) => list.forEach((s) => (out[String(s.id)] = s.name)));
    return out;
  }, [strategiesByProject]);
  const simulationsByProject = useMemo(() => {
    const out: Record<string, FireSimulation[]> = {};
    Object.entries(simulations.byProject).forEach(([pid, list]) => {
      out[pid] = list.filter((s) => !!s.simulation_id && s.status === "SUCCESS");
    });
    return out;
  }, [simulations.byProject]);

  return {
    strategiesByProject,
    strategyNames,
    simulationsByProject,
    snapshotsByProject: snapshots.byProject,
    requestProject,
    loadedProjectIds: ids,
    isPending: strategies.isPending || simulations.isPending || snapshots.isPending,
  };
}
